import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { ShowOnceSecret } from '@/components/domain/ShowOnceSecret';
import { unclaimDevice } from '@/api/endpoints/devices';
import { queryKeys } from '@/api/queryKeys';
import { errorMessage } from '@/api/errors';
import type { DeviceUnclaimed } from '@/types/api';

interface UnclaimDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deviceUid: string;
  serialNumber: string;
}

/**
 * Unlinking rotates the claim code, so the sticker in the box stops working and the
 * new code is returned exactly once. Whoever takes the device next needs it, which
 * makes this dialog the only chance to record it — hence the non-dismissible
 * confirmation step after success.
 */
export function UnclaimDialog({ open, onOpenChange, deviceUid, serialNumber }: UnclaimDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [result, setResult] = useState<DeviceUnclaimed | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const mutation = useMutation({
    mutationFn: () => unclaimDevice(deviceUid),
    onSuccess: async (data) => {
      setResult(data);
      await queryClient.invalidateQueries({ queryKey: queryKeys.devices });
      await queryClient.invalidateQueries({ queryKey: queryKeys.deviceStatus });
      await queryClient.invalidateQueries({ queryKey: queryKeys.summary });
    },
  });

  function finish() {
    setResult(null);
    setAcknowledged(false);
    onOpenChange(false);
    navigate('/vehicles');
  }

  // Unclaim always rotates, so the code is present here; the type allows null
  // because restoring a locked device deliberately does not rotate.
  if (result && result.new_claim_code) {
    return (
      <Dialog
        open={open}
        dismissible={false}
        title="Device unlinked"
        description="A new claim code has been issued. The code printed on the original card no longer works."
        footer={
          <Button variant="primary" disabled={!acknowledged} onClick={finish}>
            Done
          </Button>
        }
      >
        <div className="space-y-4">
          <ShowOnceSecret
            rows={[
              { label: 'Device ID', value: result.device_uid },
              { label: 'New claim code', value: result.new_claim_code, isClaimCode: true },
            ]}
            filename={`autoaware-${serialNumber}-claim-code`}
          />

          <label className="flex items-start gap-2 text-[13px] text-ink-secondary">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              className="mt-0.5 size-4 rounded border-line-strong text-accent focus:ring-accent-ring"
            />
            I have saved the new claim code.
          </label>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Unlink this device?"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            Unlink device
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-[13px] text-ink-secondary">
        <p>
          <span className="font-medium text-ink">{serialNumber}</span> will stop reporting to your
          account and crash detection for it will end.
        </p>
        <p>
          A fresh claim code will be issued so the device can be handed on. You will see it once, on
          the next screen.
        </p>
        {mutation.error && (
          <p className="rounded-[--radius-control] bg-bad-soft px-3 py-2 text-bad" role="alert">
            {errorMessage(mutation.error)}
          </p>
        )}
      </div>
    </Dialog>
  );
}
